'use client'

import React from 'react'
import { Settings, Plus } from 'lucide-react'
import {
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter,
  CardHeader, 
  CardTitle
} from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Button } from "@/components/ui/button"

// Helper to format currency (assuming it will be passed or imported)
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

// Define the BudgetCategory type based on usage in page.tsx
interface BudgetCategory {
  id: string;
  name: string;
  icon: string; 
  allocated: number;
  spent: number;
  color?: string;
  // items are not needed directly in this card component
}

interface BudgetCategoriesCardProps {
  budgetCategoriesData: BudgetCategory[];
  onSettingsClick: () => void;
  onAddCategoryClick: () => void;
}

export default function BudgetCategoriesCard({ 
  budgetCategoriesData, 
  onSettingsClick, 
  onAddCategoryClick 
}: BudgetCategoriesCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Budget Categories</CardTitle>
          <CardDescription>Spending progress for each category.</CardDescription>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-9 w-9"
          onClick={onSettingsClick}
        >
          <Settings className="h-5 w-5" />
          <span className="sr-only">Settings</span>
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {budgetCategoriesData.map((category) => {
          const progress = category.allocated > 0 ? (category.spent / category.allocated) * 100 : 0;
          const isOverBudget = category.spent > category.allocated;
          const remaining = category.allocated - category.spent;
          
          const indicatorClass = isOverBudget
            ? '[&>div]:bg-destructive'
            : category.color
              ? `[&>div]:bg-[${category.color}]`
              : '[&>div]:bg-primary';

          return (
            <div key={category.id} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <span className="text-lg">{category.icon}</span>
                    <span className="text-sm font-medium">{category.name}</span>
                </div>
                <span className={`text-xs font-medium ${isOverBudget ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {isOverBudget ? `${formatCurrency(Math.abs(remaining))} Over` : `${formatCurrency(remaining)} Left`}
                </span>
              </div>
              <Progress
                value={progress}
                className={`h-2 ${indicatorClass}`}
                aria-label={`${progress.toFixed(0)}% spent`}
              />
              <div className="text-xs text-muted-foreground flex justify-between">
                  <span>{formatCurrency(category.spent)}</span>
                  <span>{formatCurrency(category.allocated)}</span>
              </div>
            </div>
          );
        })}
      </CardContent>
      <CardFooter>
        <Button 
          variant="outline" 
          className="w-full"
          onClick={onAddCategoryClick}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Category
        </Button>
      </CardFooter>
    </Card>
  );
} 